import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/context/CartContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import ProductPage from "@/pages/ProductPage";
import ShopPage from "@/pages/ShopPage";
import SciencePage from "@/pages/SciencePage";
import AboutPage from "@/pages/AboutPage";
import { PrivacyPage, RefundPage, TermsPage } from "@/pages/LegalPages";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmation from "@/pages/OrderConfirmation";
import HardWaterFacewashPage from "@/pages/HardWaterFacewashPage";
import { useEffect } from "react";

function useBranding() {
  useEffect(() => {
    const BRAND_TITLE = "TRUFORM MEN — Built for Indian Climate";
    const BRAND_DESC = "TRUFORM MEN — Skincare built for Indian climate. Hard water safe, no white cast, pH-balanced formulas.";
    const ts = Date.now();

    function setTitle() {
      if (document.title !== BRAND_TITLE) {
        document.title = BRAND_TITLE;
      }
    }

    function setFavicons() {
      document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach(el => el.remove());
      const ico = document.createElement('link');
      ico.rel = 'icon';
      ico.type = 'image/x-icon';
      ico.href = '/favicon.ico?v=' + ts;
      document.head.appendChild(ico);
      const png = document.createElement('link');
      png.rel = 'icon';
      png.type = 'image/png';
      png.sizes = '32x32';
      png.href = '/truform-icon.png?v=' + ts;
      document.head.appendChild(png);
    }

    function setMeta() {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.content = BRAND_DESC;
    }

    setTitle();
    setFavicons();
    setMeta();

    // Watch for external scripts overriding the title
    const titleEl = document.querySelector('title');
    let observer;
    if (titleEl) {
      observer = new MutationObserver(() => setTitle());
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }

    // Watch for external scripts injecting different favicons
    const headObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.tagName === 'LINK' && node.rel?.includes('icon') && !node.href?.includes('truform')) {
            node.remove();
          }
        }
      }
      setTitle();
    });
    headObserver.observe(document.head, { childList: true });

    // Periodic enforcement as a safety net (first 10 seconds)
    const interval = setInterval(() => { setTitle(); }, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);

    return () => {
      if (observer) observer.disconnect();
      headObserver.disconnect();
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);
}

function App() {
  useBranding();
  return (
    <BrowserRouter>
      <CartProvider>
        <AdminAuthProvider>
          <Routes>
            {/* Admin routes (no Layout) */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            {/* Public routes */}
            <Route path="/*" element={
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/hard-water-defense-facewash" element={<HardWaterFacewashPage />} />
                  <Route path="/product/:slug" element={<ProductPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/science" element={<SciencePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/refund" element={<RefundPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </AdminAuthProvider>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
